import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Brain, ChevronRight, Scale, Users, Eye, Lock, Lightbulb, HeartHandshake } from 'lucide-react';

import ParticleBackground from '../components/ui/ParticleBackground';

const AIEthicsPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05, delayChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, type: 'spring', stiffness: 100 } }
  };

  // Subtle tech grid
  const TechGrid = () => (
    <div className="absolute inset-0 z-0">
      <div className="absolute w-full h-full grid grid-cols-12 gap-px opacity-5">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="border-r border-gray-300 h-full" />
        ))}
      </div>
      <div className="absolute w-full h-full grid grid-rows-12 gap-px opacity-5">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="border-b border-gray-300 w-full" />
        ))}
      </div>
    </div>
  );

  // Soft scanline
  const ScanLine = () => (
    <motion.div
      className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-400/10 to-transparent h-32"
      animate={{ top: ['-10%', '110%'] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
    />
  );

  const principles = [
    { icon: HeartHandshake, title: "Human-Centered", desc: "AI augments, never replaces human judgment" },
    { icon: Scale, title: "Fair & Equitable", desc: "Active bias identification and mitigation" },
    { icon: Eye, title: "Transparent", desc: "Clear documentation of AI decisions" },
    { icon: Lock, title: "Privacy-First", desc: "Data protection is foundational" },
    { icon: Users, title: "Accountable", desc: "Human oversight at all levels" },
    { icon: Lightbulb, title: "Beneficial", desc: "Clear benefit to users and society" }
  ];

  const policies = [
    {
      title: "Scope & Application",
      content: "These are the principles we commit to as Reticuli Technologies grows and begins building AI-related systems. They apply to machine learning, neural networks, neuromorphic computing, and any other AI work we take on."
    },
    {
      title: "Our Commitments",
      content: "As we build, we aim to keep a human involved in meaningful decisions, document how our systems work, assess for bias where relevant, and protect personal information. These are commitments we hold ourselves to, not certifications of an existing compliance program."
    },
    {
      title: "Transparency",
      content: "When we do deploy AI features, we intend to let people know when they're interacting with AI, and to offer a way to reach a human instead."
    },
    {
      title: "Prohibited Uses",
      content: "We will not build AI designed to deceive about its nature, enable mass surveillance targeting protected characteristics, or violate fundamental human rights."
    }
  ];

  return (
    <div className="min-h-screen bg-black text-gray-100">
      {/* Hero Section */}
      <section className="relative min-h-[45vh] flex items-center justify-center overflow-hidden bg-black">
        <ParticleBackground />
        <TechGrid />
        <ScanLine />

        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gray-700/30 to-transparent" />
        <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-r from-transparent via-gray-700/30 to-transparent" />

        <div className="container mx-auto px-4 z-20 relative pt-32 pb-20">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants} className="mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-gray-700/50 bg-gray-900/50">
                <Brain className="w-8 h-8 text-gray-400" strokeWidth={1.5} />
              </div>
            </motion.div>

            <motion.h1 
              variants={itemVariants}
              className="text-4xl md:text-5xl font-extralight tracking-wider text-white mb-3"
              style={{ fontFamily: 'Georgia, Cambria, serif' }}
            >
              AI Ethics Policy
            </motion.h1>

            <motion.p variants={itemVariants} className="text-gray-400 tracking-widest text-sm mb-4">
              RETICULI TECHNOLOGIES
            </motion.p>

            <motion.p variants={itemVariants} className="text-gray-500 text-sm mb-4">
              Our commitment to responsible, transparent, and beneficial artificial intelligence
            </motion.p>

            <motion.p variants={itemVariants} className="text-gray-500 text-sm">
              Effective: March 2, 2026
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Principles Grid */}
      <section className="relative py-12 bg-gradient-to-b from-black to-gray-950 border-b border-gray-800/30">
        <div className="container mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-xl font-light text-white text-center mb-10 tracking-wider"
            style={{ fontFamily: 'Georgia, Cambria, serif' }}
          >
            Core Principles
          </motion.h2>
          
          <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {principles.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative bg-gray-900/60 border border-gray-700/30 rounded-lg p-5 text-center hover:border-gray-600/50 transition-colors duration-300"
              >
                <p.icon className="w-8 h-8 mx-auto mb-3 text-gray-400" strokeWidth={1.5} />
                <h3 className="text-sm font-medium text-white mb-1">{p.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Policy Sections */}
      <section className="py-16 bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto space-y-10">
            {policies.map((policy, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="border-l-2 border-gray-800 pl-6 hover:border-gray-600 transition-colors"
              >
                <h2 
                  className="text-lg font-light text-white mb-3 tracking-wide" 
                  style={{ fontFamily: 'Georgia, Cambria, serif' }}
                >
                  {i + 1}. {policy.title}
                </h2>
                <p className="text-sm text-gray-400 leading-relaxed">
                  {policy.content}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 bg-gradient-to-b from-gray-950 to-black border-t border-gray-800/30">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-gray-500 mb-2 text-lg">Questions about our AI ethics practices?</p>
            <a 
              href="mailto:ethics@reticulitech.com" 
              className="text-gray-300 hover:text-white transition-colors"
            >
              ethics@reticulitech.com
            </a>
            
            <p className="text-xs text-gray-600 mt-2 mb-8">
              Reticuli Technologies • 351 St Francis St, Mobile, AL 36602
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-600 hover:to-gray-800 text-white font-medium tracking-wider rounded-lg transition-all duration-300 group text-sm border border-gray-600/30"
              >
                Contact Us
                <ChevronRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/privacy"
                className="inline-flex items-center justify-center px-8 py-3 border border-gray-700 hover:border-gray-600 text-gray-400 hover:text-white font-medium tracking-wider rounded-lg transition-all duration-300 text-sm"
              >
                Privacy Policy
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AIEthicsPolicy;